/* oxlint-disable typescript/no-explicit-any, typescript/no-unsafe-assignment -- UFile handles File | Blob | UFile | Uint8Array | object (json) via dynamic narrowing */
import { getMimeExtension } from './ufile-utils'
import { getDirPath } from './ufile-utils'
import { uid } from '../id/uid'
import { isObject, objectToBlob, blobToObject } from '../conversions'

// this info is what we pass through network
export interface UFileInfo {
  readonly type: string
  readonly size: number
  readonly path: string
  readonly meta?: Record<string, unknown>
}

export class UFile implements UFileInfo {
  id = uid() // to distinguish files with the same name (serves as a in memory pointer)
  path = ''
  blob: Blob = new Blob([])
  meta: Record<string, unknown> | undefined

  // file is File, Blob, UFile, Uint8Array, or object (json)
  constructor(file?: a, opts?: { path?: string; type?: string; meta?: a }) {
    if (!file) this.blob = new Blob([])

    if (opts?.path) this.path = opts.path
    else if (file instanceof File) this.path = file.name
    else if (file instanceof Blob)
      this.path = `${uid()}${getMimeExtension(file.type)}`
    else if (file instanceof UFile) this.path = file.path
    else if (isObject(file)) this.path = `${uid()}.json`

    if (file instanceof File) {
      this.blob = new Blob([file], { type: file.type })
      // capture lastModified into meta and path from name
      this.meta = { ...opts?.meta, lastModified: file.lastModified }
      if (!opts?.path) this.path = file.name
    } else if (file instanceof Blob)
      this.blob = new Blob([file], { type: file.type })
    else if (file instanceof UFile) {
      this.blob = file.blob
      this.meta = file.meta
      this.path = file.path
    } else if (file instanceof Uint8Array)
      // ensure correct BlobPart typing across TS DOM lib versions
      this.blob = new Blob([file as any], { type: 'application/octet-stream' })
    // should be last since ArrayBuffer is Object too
    else if (isObject(file)) this.blob = objectToBlob(file)
    // else // probably undefined

    if (opts?.type) this.blob = new Blob([this.blob], { type: opts.type })

    if (!this.meta && opts?.meta) this.meta = opts.meta
  }

  toStream(): UFileStream {
    return new UFileStream(
      {
        type: this.type,
        size: this.size,
        path: this.path,
        meta: this.meta,
      },
      this.blob,
    )
  }

  get dirPath() {
    return getDirPath(this.path)
  }

  set dirPath(dirPath: string) {
    this.path = `${dirPath}/${this.name}`
  }

  setDirPath(dirPath: string) {
    this.dirPath = dirPath
    return this
  }

  setPath(path: string) {
    this.path = path
    return this
  }

  moveTo(dirPath: string) {
    this.path = `${dirPath}/${this.path}`
    return this
  }

  makeNameUnique(id?: string) {
    this.name = `${this.fileNameWithoutExtension}-${id ?? uid()}.${this.extension}`
    return this
  }

  get name() {
    return this.path.split('/').pop() ?? ''
  }

  set name(name: string) {
    if (this.dirPath) this.path = `${this.dirPath}/${name}`
    else this.path = name
  }

  setName(name: string) {
    this.name = name
    return this
  }

  get extension() {
    return this.name.split('.').at(-1) ?? ''
  }

  set extension(ext: string) {
    this.name = `${this.fileNameWithoutExtension}.${ext}`
  }

  get fileNameWithoutExtension() {
    return this.name.split('.').slice(0, -1).join('.')
  }

  get size() {
    return this.blob.size
  }

  get type(): string {
    return this.blob.type
  }

  get isImage() {
    return this.type.startsWith('image')
  }

  get isVideo() {
    return this.type.startsWith('video')
  }

  headers(): Record<string, string> {
    return headers(this)
  }

  get isAudio() {
    return this.type.startsWith('audio')
  }

  // oxlint-disable-next-line typescript/no-unnecessary-type-parameters -- T is caller-supplied meta shape; used only for return-site narrowing
  getMeta<T>(): T {
    return this.meta as T
  }

  createObjectURL(): string {
    const url = URL as { createObjectURL?: (blob: Blob) => string }
    if (!url.createObjectURL) throw new Error('createObjectURL not supported') // on backend
    return url.createObjectURL(this.blob)
  }

  arrayBuffer(): Promise<ArrayBuffer> {
    return this.blob.arrayBuffer()
  }

  uint8Array(): Promise<Uint8Array> {
    return this.arrayBuffer().then((ab) => new Uint8Array(ab))
  }

  json<T>(): Promise<T> {
    return blobToObject(this.blob) as Promise<T>
  }

  toString() {
    return `UFile(${this.name} ${this.size} ${this.id})`
  }

  clone() {
    return new UFile(this.blob, {
      path: this.path,
      meta: this.meta,
      type: this.type,
    })
  }
}

export class UFileStream implements UFileInfo {
  info: UFileInfo
  stream: ReadableStream<Uint8Array>

  constructor(info: UFileInfo, data: Blob | ReadableStream<Uint8Array>) {
    this.info = info
    this.stream = data instanceof Blob ? data.stream() : data
  }

  headers(): Record<string, string> {
    return headers(this.info)
  }

  async toUFile(onChunk?: (chunk: Uint8Array) => void): Promise<UFile> {
    const reader = this.stream.getReader?.() // for backend only

    const chunks: Uint8Array[] = []

    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      chunks.push(value)
      onChunk?.(value)
    }

    // oxlint-disable-next-line typescript/ban-ts-comment, typescript/prefer-ts-expect-error -- intentional @ts-ignore: BlobPart typing varies across TS DOM lib versions
    // @ts-ignore
    return new UFile(new Blob(chunks), {
      path: this.info.path,
      meta: this.info.meta,
      type: this.info.type,
    })
  }

  get path() {
    return this.info.path
  }

  get size() {
    return this.info.size
  }

  get type() {
    return this.info.type
  }
}

function headers(info: UFileInfo): Record<string, string> {
  return {
    'Content-Type': info.type,
    'Content-Length': info.size.toString(),
    'X-UFile-Path': encodeURIComponent(info.path),
    ...(info.meta
      ? { 'X-UFile-Meta': encodeURIComponent(JSON.stringify(info.meta)) }
      : {}),
  }
}

export type SetUFile = (_: UFile) => void
export type UFiles = UFile[]
export type SetUFiles = (_: UFiles) => void

type a = any
